import { useAppSelector } from '../redux/hooks';

/**
 * Org-configured Member Types.
 *
 * Orgs name their own roles in Org Settings → "Name your learners / mentors /
 * admins" (stored as organization.displayRoles). RDP Creative, for example,
 * calls learners "Employee", mentors "Manager" and admins "Admin". Anywhere we
 * show a member's role we should use those words, not the BE's canonical role
 * strings ("mentee", "mentor, program_admin", …).
 *
 * Org admins are deliberately NOT renameable: that role is platform-level
 * rather than a program role, so it always reads "Org Admin".
 */
export interface IDisplayRoles {
  admin?: string | string[];
  mentee?: string | string[];
  mentor?: string | string[];
}

export const ORG_ADMIN_LABEL = 'Org Admin';

// displayRoles values arrive either as a plain string or a single-item array.
// Orgs type these words however they like — RDP Creative stored "employee".
// The label sits in a picker beside "Admin", so an all-lowercase word gets its
// first letter raised; anything the org already capitalised is left exactly as
// typed, so "SLT" and "HR Lead" survive.
export const pickLabel = (value: unknown, fallback: string): string => {
  const raw = Array.isArray(value) ? value[0] : value;
  const word = typeof raw === 'string' ? raw.trim() : '';
  if (!word) return fallback;
  return word === word.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word;
};

// "program_admin" → "Program Admin". Used for role words we don't recognise so
// they still read as labels rather than raw BE strings.
const humanizeRole = (role: string): string =>
  role
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// Canonical BE role words grouped by the display role they belong to. The
// learner/mentor lists mirror the option lists in Org Settings
// (OrganizationInformation.tsx MENTEE_OPTIONS / MENTOR_OPTIONS), so an org that
// picked "employee" as its learner word still resolves to the learner label.
const LEARNER_ROLES = ['mentee', 'learner', 'client', 'employee', 'student'];
const MENTOR_ROLES = ['mentor', 'coach', 'consultant', 'manager', 'buddy', 'faculty'];
const ADMIN_ROLES = ['admin', 'staff', 'program_admin', 'programadmin', 'group_admin', 'groupadmin'];
const ORG_ADMIN_ROLES = ['organization_admin', 'organisation_admin', 'org_admin', 'orgadmin'];

/**
 * Turns a BE member type ("mentee", "mentor, program_admin", "organization_admin")
 * into the org's own labels, e.g. "Employee", "Manager, Admin", "Org Admin".
 * Duplicates are collapsed — a program admin who is also an org admin shouldn't
 * read "Admin, Admin".
 */
export const formatMemberType = (
  memberType: string | undefined | null,
  displayRoles: IDisplayRoles | undefined,
  fallback = 'Not set'
): string => {
  if (!memberType || !memberType.trim()) return fallback;

  const learnerLabel = pickLabel(displayRoles?.mentee, 'Learner');
  const mentorLabel = pickLabel(displayRoles?.mentor, 'Mentor');
  const adminLabel = pickLabel(displayRoles?.admin, 'Admin');

  const labels = memberType
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const key = part.toLowerCase().replace(/\s+/g, '_');
      if (ORG_ADMIN_ROLES.includes(key)) return ORG_ADMIN_LABEL;
      if (ADMIN_ROLES.includes(key)) return adminLabel;
      if (MENTOR_ROLES.includes(key)) return mentorLabel;
      if (LEARNER_ROLES.includes(key)) return learnerLabel;
      return humanizeRole(part);
    });

  const unique = labels.filter((label, i) => labels.indexOf(label) === i);
  return unique.length ? unique.join(', ') : fallback;
};

/**
 * Hook form: reads the current org's display roles from redux and returns a
 * formatter. Use this in member lists so the "Member" column speaks the org's
 * language.
 */
export const useMemberTypeFormatter = () => {
  const displayRoles = useAppSelector((state) => state?.orgDetails?.data?.displayRoles) as IDisplayRoles | undefined;
  return (memberType: string | undefined | null, fallback = 'Not set') =>
    formatMemberType(memberType, displayRoles, fallback);
};

/**
 * The org's three role words, ready for a label. Same source and fallbacks as
 * formatMemberType, exposed on its own for pickers and copy that need the
 * singular word rather than a formatted member type.
 *
 * These are LABELS. The canonical role values ("mentee", "mentor", "admin")
 * are what the backend stores and compares, and they must keep travelling in
 * payloads untouched — never send one of these strings to an API.
 */
export const useRoleLabels = (): { mentee: string; mentor: string; admin: string } => {
  const displayRoles = useAppSelector((state) => state?.orgDetails?.data?.displayRoles) as IDisplayRoles | undefined;
  return {
    mentee: memberWord('mentee', displayRoles, { fallback: 'Learner' }),
    mentor: memberWord('mentor', displayRoles),
    admin: memberWord('admin', displayRoles)
  };
};

export type MemberRole = 'mentee' | 'mentor' | 'admin';

export interface MemberWordOptions {
  /** The plural form ("Employees", "Coaches"). */
  plural?: boolean;
  /** Singular when 1, plural otherwise. Wins over `plural`. */
  count?: number;
  /** Mid-sentence form: "employee", but "SLT" and "HR Lead" stay as typed. */
  lower?: boolean;
  /** Prefixes "a" / "an": "an employee", "a coach". */
  article?: boolean;
  /** "employee's", "employees'". */
  possessive?: boolean;
  /** The singular word when the org has none stored. */
  fallback?: string;
}

/**
 * What a screen shows when the org has no word stored (or before the org has loaded): the words the
 * screens used before they read the setting, so an org without words sees no change.
 */
const DEFAULT_WORD: Record<MemberRole, string> = { mentee: 'Mentee', mentor: 'Mentor', admin: 'Admin' };
const IRREGULAR_PLURALS: Record<string, string> = { staff: 'staff', faculty: 'faculty', person: 'people' };

/** Plural of the last word only, keeping the case it was typed in: Coach → Coaches, SLT → SLTs. */
const pluralize = (phrase: string): string => {
  const m = phrase.match(/^(.*?)(\S+)$/);
  if (!m) return phrase;
  const [, head, word] = m;
  const irregular = IRREGULAR_PLURALS[word.toLowerCase()];
  if (irregular)
    return (
      head + (word[0] === word[0].toUpperCase() ? irregular.charAt(0).toUpperCase() + irregular.slice(1) : irregular)
    );
  if (word.length > 1 && word === word.toUpperCase() && /[A-Z]/.test(word)) return `${head}${word}s`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${head}${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${head}${word.slice(0, -1)}ies`;
  return `${head}${word}s`;
};

/**
 * The org's word for a member type, in the form a sentence needs. Every screen
 * that names learners (mentees), mentors or admins should go through this so
 * the word an org sets in Settings → Members shows everywhere.
 *
 *   memberWord('mentee', { mentee: 'employee' })                    → "Employee"
 *   memberWord('mentor', { mentor: 'coach' }, { plural: true })      → "Coaches"
 *   memberWord('mentee', { mentee: 'employee' }, { lower: true, article: true }) → "an employee"
 *   memberWord('mentor', undefined, { count: 2, lower: true })       → "mentors"
 *
 * Labels only: the canonical codes ("mentee", "mentor", "admin") stay the
 * values sent to the API and compared in code.
 */
export const memberWord = (
  role: MemberRole,
  displayRoles: IDisplayRoles | undefined,
  opts: MemberWordOptions = {}
): string => {
  let word = pickLabel(displayRoles?.[role], opts.fallback ?? DEFAULT_WORD[role]);
  const plural = opts.count !== undefined ? opts.count !== 1 : Boolean(opts.plural);
  if (plural) word = pluralize(word);
  if (opts.lower && word.slice(1) === word.slice(1).toLowerCase()) word = word.charAt(0).toLowerCase() + word.slice(1);
  if (opts.possessive) word = plural && /s$/i.test(word) ? `${word}'` : `${word}'s`;
  if (opts.article) word = `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;
  return word;
};

/** Hook form of memberWord, reading the current org's words from redux. */
export const useMemberWord = () => {
  const displayRoles = useAppSelector((state) => state?.orgDetails?.data?.displayRoles) as IDisplayRoles | undefined;
  return (role: MemberRole, opts?: MemberWordOptions) => memberWord(role, displayRoles, opts);
};

/** Whether an API role code ("mentee", "Mentor", " admin ") is one of the three member types. */
export const isMemberRole = (code: unknown): code is MemberRole =>
  typeof code === 'string' && ['mentee', 'mentor', 'admin'].includes(code.trim().toLowerCase());

export default formatMemberType;
